import React, {useEffect, useMemo} from 'react';
import Form from 'react-bootstrap/Form';
import {CSSObject} from '@emotion/react';
import Select from 'react-select';
import useData from '@/utils/useData';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import {ChartQuery} from 'entities/chart';
import {FormikT, FormikTValues} from 'utils/types';
import {aggregationChoicesFilters, EventFilter, EventFilterGroup} from '@/views/storyboard/utils/chartConstants';
import {OptionProps} from 'react-select/dist/declarations/src/components/Option';
import {SingleValue} from 'react-select/dist/declarations/src/types';
import i18n from 'i18n';
import Icon from '@/components/icons/Icon';
import {resolveRestrictedFields} from '@/utils/fieldRegistry';


interface Option {
    value: string;
    label: string;
    type?: string;
    isDisabled?: boolean;
}

interface Props {
    values: FormikTValues<ChartQuery>;
    setFieldValue: FormikT<ChartQuery>['setFieldValue'];
    label?: string;
    eventFilters?: EventFilter[];
    eventFiltersLoading?: boolean;
}

export const ChartAggregationsSelect = React.memo<Props>((
    {
        values,
        setFieldValue,
        label = i18n.t('jt_aggregate_based.text'),
        eventFilters: eventFiltersProp,
        eventFiltersLoading,
    },
) => {
    const [eventFilterGroupList, loadingFromData] = useData<EventFilterGroup>(window.URLS.eventFiltersResource, {
        storeResolver: 'eventFilters',
        actionResolver: 'loadEventFilters',
        useCachedData: true,
    });

    const {userData} = useOptimizedConfig();
    const restrictedFields: string[] = useMemo(() => resolveRestrictedFields(userData?.restrictedFieldKeys), [userData?.restrictedFieldKeys]);

    const isRestricted = (filterValue: string) => {
        if (!restrictedFields.length) return false;
        const fieldName = filterValue.replace('all_fields.', '').replace('.keyword', '');
        return restrictedFields.includes(fieldName);
    };

    const eventFilters: EventFilter[] = eventFiltersProp ?? [
        ...(eventFilterGroupList.core || []),
        ...(eventFilterGroupList.prioritised || []),
        ...(eventFilterGroupList.non_prioritised || []),
        ...(eventFilterGroupList.others || []),
    ];
    const loading = eventFiltersProp !== undefined ? (eventFiltersLoading ?? false) : loadingFromData;

    useEffect(() => {
        if (loading || !values.output || eventFilters.length === 0) {
            return;
        }

        const availableOptions = aggregationChoicesFilters[values.output ?? 'default'](eventFilters)
            .filter((el) => el[4] === true && !isRestricted(el[0]));

        if (!values.aggs_item) {
            const nextValue = availableOptions[0]?.[0] ?? '';
            if (nextValue && nextValue !== values.aggs_item) {
                setFieldValue('aggs_item', nextValue, false);
            }
        }
    }, [loading, eventFilters, values.output, values.aggs_item, setFieldValue, restrictedFields]);

    return (
        <Form.Group className="cs-field" controlId="aggsItem" >
            <label className="cs-field__label">{label}</label>
            <Select<Option>
                id="chart-settings-form-aggregate-on"
                value={values.aggs_item ? {
                    value: values.aggs_item,
                    label: eventFilters.find((x) => x[0] === values.aggs_item)?.[1] ?? '',
                } : null}
                name="aggs_item"
                onChange={(value: SingleValue<Option>) => {
                    if (value?.isDisabled) return;
                    setFieldValue('aggs_item', value?.value);
                }}
                isLoading={loading && !values.aggs_item}
                loadingMessage={() => i18n.t('jt_loading_opt.text')}
                placeholder={i18n.t('chart_settings_choose_field.text')}
                maxMenuHeight={180}
                menuPlacement="auto"
                menuShouldScrollIntoView={true}
                isOptionDisabled={(option) => !!option.isDisabled}
                options={aggregationChoicesFilters[values.output ?? 'default'](eventFilters)
                    .filter((el) => el[4] === true)
                    .map((el) => ({
                        value: el[0],
                        label: el[1],
                        type: el[2],
                        isDisabled: isRestricted(el[0]),
                    }))}
                formatOptionLabel={(option) => (
                    <span className="d-inline-flex align-items-center">
                        {option.label}
                        {option.isDisabled && <Icon name="mdi-lock" size="0.85em" className="ml-1" />}
                    </span>
                )}
                styles={{
                    control: (base: CSSObject): CSSObject => ({...base, minHeight: 34, height: 34}),
                    valueContainer: (base: CSSObject): CSSObject => ({...base, padding: '0 8px'}),
                    indicatorsContainer: (base: CSSObject): CSSObject => ({...base, height: 34}),
                    menu: (base: CSSObject): CSSObject => ({...base, marginBottom: 8}),
                    menuList: (base: CSSObject): CSSObject => ({...base, maxHeight: 180}),
                    option: (base: CSSObject, state: OptionProps<Option, false>): CSSObject => {
                        let additionalStyles: CSSObject;
                        if (state.data?.isDisabled) {
                            additionalStyles = {
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                color: '#999',
                                cursor: 'not-allowed',
                            };
                        } else if (!state.isFocused) {
                            additionalStyles = {
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            };
                        } else {
                            additionalStyles = {
                                wordBreak: 'break-word',
                            };
                        }
                        return {
                            ...base,
                            background: !state.data?.isDisabled && state.isFocused
                                ? 'rgba(0, 158, 251, 0.08)'
                                : 'transparent',
                            backgroundColor: !state.data?.isDisabled && state.isFocused
                                ? 'rgba(0, 158, 251, 0.08)'
                                : 'transparent',
                            color: 'inherit',
                            ':active': {
                                ...base[':active'],
                                backgroundColor: !state.data?.isDisabled
                                    ? 'rgba(0, 158, 251, 0.08)'
                                    : 'transparent',
                            },
                            ...additionalStyles,
                        };
                    },
                }}
            />
        </Form.Group >
    );
});

